import { AuthState, RootState } from '@/store/storeStateInterfaces';
import { ActionTree, GetterTree, Module, MutationTree } from 'vuex';
import AuthRepository from '@/api/repositories/AuthRepository';
import { RepositoryFactory } from '@/api/RepositoryFactory';
import User from '@/models/User';
import Location from '@/models/Location';
import { LocalizationResponse } from '@/interfaces/LocalizationResponse';
import { Vue } from 'vue-property-decorator';
import TokenInformation from '@/models/TokenInformation';

const authRepository: AuthRepository = RepositoryFactory.get('auth');

const store: AuthState = {
    token: undefined,
    bridge: undefined,
    currentUser: undefined,
    currentLocation: null,
    availableLocations: [],
    localizationTimeOut: false
};

const actions: ActionTree<AuthState, RootState> = {
    /**
     * Checks if user credentials are available. If not query backend for incomplete user credentials and save them.
     * This case happens when the local storage is empty.
     * @param commit
     */
    initializeUser: async ({ commit, getters }): Promise<void> => {
        if (!getters.currentUser) {
            const response = await authRepository.getIncompleteUser();
            const user = User.parseFromObject(response.data.response);
            commit('saveUser', user);
        }
    },
    /**
     * Makes a login request. Uses saved currentUser for login. Make sure that was called before.
     * @param commit
     * @param getters
     */
    login: async ({ commit, getters }): Promise<void> => {
        // the login requests gets the needed Session ID and saves it in document ( this happens automatically)
        // get additional params

        const user = getters.currentUser;
        const response = await authRepository.login({
            email: user.email,
            password: user.password,
            referrer: document.referrer,
            token: getters.token,
            fingerprint: await Vue.getFingerprint()
        });
        // add missing user properties
        const newUser = User.parseFromObject({ ...user, ...response.data.response });
        commit('saveUser', newUser);
    },
    logout: async ({ commit }): Promise<void> => {
        await authRepository.logout();
    },
    /**
     * Performs a request to localize the web reader. If locations are available, return them
     * for selecting. If not, reset currentLocation
     * @param commit
     * @param getters
     * @param token
     */
    localization: async ({ commit, getters }, id_location?: string): Promise<LocalizationResponse> => {
        const payload = {
            id_location: id_location || getters.currentLocation?.id,
            referrer: document.referrer,
            token: getters.token,
            fingerprint: await Vue.getFingerprint()
        };
        const response = await authRepository.localization(payload);
        const result: LocalizationResponse = {
            code: response.data.code,
            locations: undefined
        };
        // 501 means locations are available, 511 means you are in the given location
        if (result.code === 501 || result.code === 511) {
            result.locations = Location.parseFromArray(response.data.data) as Location[];
        }
        // other codes are
        //      code: 510, "you are not in the given location."
        //      code: 500, "you are not near a location."
        //      code: 520, "your token is invalid"
        return result;
    },
    /**
     * Confirms localized location
     * @param commit
     * @param getters
     * @param token
     */
    confirmLocation: async ({ commit, getters }, id_location?: string): Promise<Location> => {
        const payload = {
            id_location: id_location || getters.currentLocation?.id,
            referrer: document.referrer,
            token: getters.token,
            fingerprint: await Vue.getFingerprint()
        };
        const response = await authRepository.localization(payload);
        // 511 means you are in the given location
        if (response.data.code === 511) {
            const confirmedLocation = Location.parseFromObject(response.data.data[0]);
            commit('saveLocation', confirmedLocation);
            return confirmedLocation;
        } else {
            // this cannot happen under normal circumstances
            throw new Error('location confirmation could not be performed');
        }
    },
    getTokenInformation: async ({ commit }, token: string): Promise<TokenInformation> => {
        const payload = {
            referrer: document.referrer,
            token,
            fingerprint: await Vue.getFingerprint()
        };
        const response = await authRepository.getTokenInformation(payload);
        const tokenInformation = TokenInformation.parseFromObject(response.data);
        return tokenInformation;
    }
};

const mutations: MutationTree<AuthState> = {
    setToken: (state: AuthState, token: string | undefined) => {
        state.token = token;
    },
    setBridge: (state: AuthState, bridge: string | undefined) => {
        state.bridge = bridge;
    },
    saveUser: (state: AuthState, user: User) => {
        state.currentUser = user;
    },
    saveLocation: (state: AuthState, location: Location) => {
        state.currentLocation = location;
    },
    setAvailableLocations: (state: AuthState, locations: Location[]) => {
        state.availableLocations = locations;
    },
    setLocalizationTimeOut: (state: AuthState, value: boolean) => {
        state.localizationTimeOut = value;
    }

};

const getters: GetterTree<AuthState, RootState> = {
    token: (state: AuthState) => state.token,
    bridge: (state: AuthState) => state.bridge,
    currentUser: (state: AuthState) => state.currentUser,
    currentLocation: (state: AuthState) => state.currentLocation,
    availableLocations: (state: AuthState) => state.availableLocations,
    localizationTimeOut: (state: AuthState) => state.localizationTimeOut
};

const authStore: Module<AuthState, RootState> = {
    state: store,
    actions,
    mutations,
    getters
};

export default authStore;
